import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import React, { useState } from 'react';
import { useAddMedicalHistory } from 'infrastructure/hooks/ccm';
import MedicalHistoryForm from 'components/molecules/admin-panel/forms/medical-history';
import SegmentedTabs from 'components/atoms/oneplatform/segmented-tabs';
import ConditionGoals from 'components/molecules/conditions-goals';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { Button } from 'antd';
import {
  FileAddOutlined,
  ReadOutlined,
  DotChartOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';

import MedicalHistoryTable from './components/medical-table';
import s from './styles.module.scss';
import MasterModal from '../masterModal';
import PatientAllergyLog from '../allergy-log';

import type { FC } from 'react';
import type { Values } from 'components/molecules/admin-panel/forms/medical-history/validation-schema';
import type { IBaseTab } from 'components/atoms/oneplatform/segmented-tabs';

interface PastMedicalHistoryProps {
  patientGuid: string;
}

const PastMedicalHistory: FC<PastMedicalHistoryProps> = ({ patientGuid }) => {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const { t } = useTranslation();
  const { addMedicalHistory, addLoading } = useAddMedicalHistory();
  const { isReadOnlyRole } = useCheckRoleAccess();

  const toggleOpen = () => setOpen((prev) => !prev);

  const onSubmit = async ({ record }: Values) => {
    if (record)
      addMedicalHistory(
        { patientGuid, record },
        {
          onSuccess: () => {
            setOpen(false);
            setRefreshToken((prev) => prev + 1);
          },
        },
      );
  };

  const onCancelForm = () => {
    setOpen(false);
  };

  const tabs: IBaseTab[] = [
    {
      key: 0,
      label: (
        <>
          <FileAddOutlined /> {t('labels.conditions')}
        </>
      ),
      children: (
        <ConditionGoals
          patientGuid={patientGuid}
          isSingle
          disableActions={isReadOnlyRole}
        />
      ),
    },
    {
      key: 1,
      label: (
        <>
          <ReadOutlined /> {t('labels.pastMedicalHistory')}
        </>
      ),
      children: (
        <>
          <div className={s['button-wrapper']}>
            <BaseButton
              label={`+ ${t('labels.addHistory')}`}
              dataCy="addHistory-button"
              onClick={toggleOpen}
              loading={addLoading}
            />
          </div>
          <MedicalHistoryTable
            patientGuid={patientGuid}
            page={page}
            setPage={setPage}
            disableActions={addLoading}
            refreshToken={refreshToken}
          />
        </>
      ),
    },
    {
      key: 2,
      label: (
        <>
          <DotChartOutlined /> {t('labels.allergyLog')}
        </>
      ),
      children: <PatientAllergyLog patientGuid={patientGuid} />,
    },
  ];

  return (
    <>
      <div className={s.wrapper} id="medical-history">
        <div className={s['title-wrapper']}>
          <Button
            type="text"
            className={s['toggle-button']}
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <h4 className={s.title}>{t('labels.patientHistory')}</h4>
        </div>
        {isExpanded && (
          <SegmentedTabs
            tabs={tabs}
            defaultIndex={activeTab}
            onChange={(_, index) => setActiveTab(index)}
            type="rounded"
            size="small"
            wrapperClassName={s['segmented-tabs-wrapper']}
          />
        )}
      </div>
      {open && (
        <MasterModal
          title={t('labels.addHistory')}
          isOpen={open}
          toggle={toggleOpen}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <MedicalHistoryForm
            loading={addLoading}
            onCancelForm={onCancelForm}
            onSubmit={onSubmit}
          />
        </MasterModal>
      )}
    </>
  );
};
export default PastMedicalHistory;
